import { useState, useEffect, useRef, FC, SyntheticEvent } from 'react';
import { useIntl } from 'react-intl';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';

import { useStyles } from './simulator-tabs.styles';
import { TabPanel } from '../../../common/tab-panel';
import { SimulatorTabProps } from '../simulator.models';
import { SimulatorBenefit } from '../benefit/benefit';

export const SimulatorTabs: FC<{ tabs: SimulatorTabProps[] }> = ({ tabs }) => {
  const [value, setValue] = useState(0);
  const [circleSize, setCircleSize] = useState(150);

  const [relativePos, setRelativePos] = useState({ xPercent: 0.5, yPercent: 0.5 });
  const [maskPos, setMaskPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const containerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const classes = useStyles();
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const getOrientation = () =>
    window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait';
  const [orientation, setOrientation] = useState(getOrientation());

  useEffect(() => {
    const handleResize = () => {
      setOrientation(getOrientation());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setCircleSize(200);
      if (orientation === 'landscape') {
        setRelativePos({ xPercent: 0.25, yPercent: 0.3 });
      } else {
        setRelativePos({ xPercent: 0.25, yPercent: 0.35 });
      }
    } else if (isTablet) {
      setCircleSize(200);
      if (orientation === 'landscape') {
        setRelativePos({ xPercent: 0.33, yPercent: 0.25 });
      } else {
        setRelativePos({ xPercent: 0.38, yPercent: 0.31 });
      }
    } else {
      setCircleSize(300);
      setRelativePos({ xPercent: 0.4, yPercent: 0.25 });
    }
  }, [isMobile, isTablet, isDesktop, orientation]);

  useEffect(() => {
    const container = containerRefs.current[value];
    if (!container) return;
    const updateMaskPos = () => {
      const { width, height } = container.getBoundingClientRect();
      const x = relativePos.xPercent * width;
      const y = relativePos.yPercent * height;
      setMaskPos({ x, y });
    };

    const observer = new ResizeObserver(updateMaskPos);
    observer.observe(container);
    updateMaskPos();
    return () => observer.disconnect();
  }, [relativePos, value]);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  function a11yProps(index: number) {
    return {
      id: `tab-${index}`,
      'aria-controls': `tabpanel-${index}`,
    };
  }

  const getClickPosition = (
    e: React.MouseEvent<HTMLDivElement>,
    container: HTMLDivElement,
    size: number,
  ) => {
    const rect = container.getBoundingClientRect();

    const clickX = e.clientX - rect.left - size / 2;
    const clickY = e.clientY - rect.top - size / 2;

    const clampedX = Math.max(0, Math.min(clickX, rect.width - size));
    const clampedY = Math.max(0, Math.min(clickY, rect.height - size));

    return {
      clamped: { x: clampedX, y: clampedY },
      percent: {
        x: clampedX / rect.width,
        y: clampedY / rect.height,
      },
    };
  };

  return (
    <div className={classes.root}>
      <Tabs
        value={value}
        onChange={handleChange}
        indicatorColor="secondary"
        textColor="inherit"
        variant="fullWidth"
        aria-label="full width tabs example"
      >
        {tabs.map((tabItem, index) => (
          <Tab key={index} label={intl.formatMessage({ id: tabItem.name })} {...a11yProps(index)} />
        ))}
      </Tabs>
      {tabs.map((tabItem, index) => (
        <TabPanel value={value} index={index} key={index}>
          <div className={classes.wrapper}>
            <div
              className={classes.imageWrapper}
              ref={(el) => (containerRefs.current[index] = el)}
              onClick={(e) => {
                const container = containerRefs.current[value];
                if (!container) return;

                const { percent } = getClickPosition(e, container, circleSize);

                setRelativePos({ xPercent: percent.x, yPercent: percent.y });
              }}
            >
              <img
                src={tabItem.background}
                className={classes.baseImage}
                alt=""
                width="425"
                height="865"
              />
              <div
                className={classes.maskedBlur}
                style={{
                  WebkitMaskImage: `radial-gradient(circle ${circleSize / 2}px at ${
                    maskPos.x + circleSize / 2
                  }px ${maskPos.y + circleSize / 2}px, transparent 100%, black 100%)`,
                  maskImage: `radial-gradient(circle ${circleSize / 2}px at ${
                    maskPos.x + circleSize / 2
                  }px ${
                    maskPos.y + circleSize / 2
                  }px, transparent 0%, transparent 99%, black 99%, black 100%)`,
                }}
              >
                <img
                  src={tabItem.backgroundBlur}
                  className={classes.blurImage}
                  alt=""
                  width="425"
                  height="865"
                />
              </div>
              <Draggable
                position={maskPos}
                bounds="parent"
                onStart={() => setIsDragging(true)}
                onStop={() => setIsDragging(false)}
                onDrag={(e: DraggableEvent, data: DraggableData) => {
                  const container = containerRefs.current[value];
                  if (!container) return;
                  const { width, height } = container.getBoundingClientRect();
                  setRelativePos({
                    xPercent: data.x / width,
                    yPercent: data.y / height,
                  });
                }}
              >
                <div
                  className={`${classes.draggableCircle} ${isDragging ? classes.dragging : ''}`}
                  style={{
                    width: circleSize,
                    height: circleSize,
                    pointerEvents: 'auto',
                  }}
                />
              </Draggable>
            </div>

            <div className={classes.panelInner}>
              <ul className={classes.list}>
                {tabItem.benefits.map(({ content, arrowPosition, position }, i) => (
                  <li key={i}>
                    <SimulatorBenefit
                      content={content}
                      arrowPosition={arrowPosition}
                      position={position}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </TabPanel>
      ))}
    </div>
  );
};
